import SolidBedIcon from 'LuxKit/Icons/solid/SolidBedIcon'
import { setSvgIconSize } from 'LuxKit/SvgIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import { useAppSelector } from 'hooks/reduxHooks'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import { rem } from 'polished'
import React from 'react'
import styled from 'styled-components'
import LoyaltyTierBenefitAvailabilityMessageBanner from '../LoyaltyTierBenefitAvailabilityMessageBanner'
import LineCrownIcon from 'LuxKit/Icons/line/LineCrownIcon'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import Heading from 'LuxKit/Typography/Heading'
import useLoyaltyUserBenefitView from 'loyalty/hooks/useLoyaltyUserBenefitView'
import LoyaltyTierIcon from 'LuxKit/Icons/loyalty/LoyaltyTierIcon'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import { getSearchQueryFromFilter } from 'lib/search/searchUtils'
import Places from 'constants/places'
import LoyaltyProgramNameLogo from '../LoyaltyProgramNameLogo'
import LoyaltyTierLabel from 'components/Common/Labels/LoyaltyTierLabel'
import Typography from 'LuxKit/Typography/Typography'

const Hero = styled(Sheet)`
  padding: ${rem(32)} ${rem(24)};

  &.tier-bronze {
    background: ${props => props.theme.palette.product.loyalty.bronze.gradientPrimaryBackground};
  }
  &.tier-silver {
    background: ${props => props.theme.palette.product.loyalty.silver.gradientPrimaryBackground};
  }
  &.tier-gold {
    background: ${props => props.theme.palette.product.loyalty.gold.gradientPrimaryBackground};
  }
  &.tier-platinum {
    background: ${props => props.theme.palette.product.loyalty.platinum.gradientPrimaryBackground};
  }
`

const IconContainer = styled(Sheet)`
  display: grid;
  place-items: center;
  height: ${rem(32)};
  width: ${rem(32)};
  flex-shrink: 0;
  ${setSvgIconSize(20)}
`

const FILTERS: App.OfferListFilters = {
  destinations: [Places.Anywhere.id],
  loyalty: ['Free Room Upgrade'],
}

const OFFERS_LINK = `/search?${getSearchQueryFromFilter(FILTERS)}`

function LoyaltyStayUpgradePromoBenefitModal() {
  const account = useAppSelector(state => state.loyalty.account.data)
  const [tier, tierName] = useCurrentLoyaltyTier()
  const givenName = useAppSelector(state => state.auth.account.givenName)
  const upgradeBenefit = useLoyaltyUserBenefitView('hotel_upgrade')
  const userHasAllUpgradesRemaining = upgradeBenefit?.availability === upgradeBenefit?.benefit.limit

  if ((tier === 'bronze') || (tier === 'platinum')) return null

  return <ProductPaletteProvider palette={`tier-${tier}`}>
    <ModalBase>
      <ModalHeader title="" isOverlay />
      <ModalBody>
        <Hero className={`tier-${tier}`}>
          <VerticalSpacer gap={16}>
            <VerticalSpacer gap={12}>
              <Group direction="horizontal" gap={12} verticalAlign="center">
                <LoyaltyProgramNameLogo height={24} />
                {account && <LoyaltyTierLabel kind="primary" tier={tier}>
                  <Typography format="uppercase" as="span">{tierName}</Typography>
                  {' '}· Member {account.accountNumber}
                </LoyaltyTierLabel>}
              </Group>
              <BodyText variant="small">The ultimate rewards program to elevate every escape</BodyText>
            </VerticalSpacer>
            <Heading variant="heading1">
              {tier === 'silver' && <>
                {!!givenName && <><i>{givenName}</i>, you’ve got a free upgrade</>}
                {!givenName && <>You’ve got a free upgrade</>}
              </>}
              {tier === 'gold' && <>
                {!!givenName && userHasAllUpgradesRemaining && <><i>{givenName}</i>, you’ve now got 2 free upgrades</>}
                {!givenName && userHasAllUpgradesRemaining && <>You’ve now got 2 free upgrades</>}
                {!!givenName && !userHasAllUpgradesRemaining && <><i>{givenName}</i>, you’ve now got another free upgrade</>}
                {!givenName && !userHasAllUpgradesRemaining && <>You’ve now got another free upgrade</>}
              </>}
            </Heading>
          </VerticalSpacer>
        </Hero>
        <ModalContent>
          <Group direction="vertical" gap={24}>
            <BodyText variant="medium">
              {tier === 'silver' && <><b>Your {tierName} benefits</b> now includes a room or package upgrade to use each membership year on select offers</>}
              {tier === 'gold' && <><b>Your {tierName} benefits</b> now include 2 room or package upgrades to use each membership year on select offers.</>}
            </BodyText>
            <VerticalSpacer gap={16}>
              <Sheet padding={12} border="neutral-six" borderRadius="L">
                <Group direction="horizontal" verticalAlign="start" gap={12}>
                  <IconContainer background={`tier-${tier}-dark`} borderRadius="round">
                    <LineCrownIcon />
                  </IconContainer>
                  <div>
                    <BodyText variant="large" weight="bold">Free package upgrades</BodyText>
                    <BodyText variant="large">
                      Get more from your escape with extras like return transfers, spa treatments or daily dinners.
                    </BodyText>
                  </div>
                </Group>
              </Sheet>
              <Sheet padding={12} border="neutral-six" borderRadius="L">
                <Group direction="horizontal" verticalAlign="start" gap={12}>
                  <IconContainer background={`tier-${tier}-dark`} borderRadius="round">
                    <SolidBedIcon />
                  </IconContainer>
                  <div>
                    <BodyText variant="large" weight="bold">Free room upgrades</BodyText>
                    <BodyText variant="large">
                      Stay in style with upgrades to bigger rooms, better views or premium locations, guaranteed.
                    </BodyText>
                  </div>
                </Group>
              </Sheet>
            </VerticalSpacer>
            <LoyaltyTierBenefitAvailabilityMessageBanner benefitType="hotel_upgrade" flat icon={<LoyaltyTierIcon tier={tier} size="M" />} />
          </Group>
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: 'View offers',
          to: OFFERS_LINK,
        }}
        secondaryActionProps={{
          children: `${tierName} benefits`,
          to: `/${LOYALTY_LANDING_PAGE}#benefits`,
        }}
      />
    </ModalBase>
  </ProductPaletteProvider>
}

export default LoyaltyStayUpgradePromoBenefitModal
